import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DownloadPageSize, DropdownStatusList, FormTypeAdd, FormTypeEdit, PageSize, SalesStockGroupByOptions, SalesStockStatusOptions, StatusCodeActive } from "utils/Constant";
import Dropdown from "components/Input/Dropdown";
import { CheckUndefined, GetStatus, money } from "utils/helpers"
import { useModal } from "context/ModalContext";
import Select from 'react-select'
import SmallButton from "components/Button/SmallButton";
import { FilterCard, MainBodyCard, PagesTitle } from "components/Cards";
import Pagination from "components/Input/PaginationButton";
import { SalesStockService } from "services/SalesStockService/SalesStockService";
import GridEditButton from "components/Button/GridEditButton";
import GridDeleteButton from "components/Button/GridDeleteButton";
import { BrandService } from "services/BrandService/BrandService";
import DataTable from "components/DataTable";
import AddButton from "components/Button/AddButton";
import PrintButton from "components/Button/PrintButton";
import { exportSalesStockToPdf } from "utils/export/ExportSalesStock";

export default function SalesStock() {
    let navigate = useNavigate()

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchBrandList } = BrandService()
    const { fetchSalesStockList, fetchDeleteSalesStock } = SalesStockService()

    const firstLoad = useRef(true)
    const [productName, setProductName] = useState('')
    const [stockCode, setStockCode] = useState('')
    const [stockStatus, setStockStatus] = useState(SalesStockStatusOptions[1].value)
    const [groupBy, setGroupBy] = useState(SalesStockGroupByOptions[0].value)
    const [brandOptions, setBrandOptions] = useState('')
    const [brand, setBrand] = useState('')
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [salesStockList, setSalesStockList] = useState([])
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState('')

    const columns = groupBy === '1' ?
        [
            {
                key: "rowNumber",
                header: "No",
                headerClassName: "w-16 font-semibold",
                cellClassName: "text-center",
            },
            {
                key: "stockCode",
                header: "Kode Stock"
            },
            {
                key: "productName",
                header: "Nama Product"
            },
            {
                key: "size",
                header: "Size"
            },
            {
                key: "purchasePrice",
                header: "Purchase Price",
                render: (row) => 'Rp ' + money(row.purchasePrice),
            },
            {
                key: "stockStatus",
                header: "Stock Status",
                render: (row) => SalesStockStatusOptions.find(x => x.value === String(row.stockStatus)).label
            },
            {
                key: "status",
                header: "Status",
                render: (row) => GetStatus(row.status),
            },
            {
                key: "action",
                header: "Action",
                render: (row) => (
                    <div className="flex gap-2">
                        <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.salesStockId)} size="w-3" />
                        <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.salesStockId, row.status)} size="w-3" />
                    </div>

                ),
            },
        ] :
        [
            {
                key: "rowNumber",
                header: "No",
                headerClassName: "w-16 font-semibold",
                cellClassName: "text-center",
                render: (row, index) => index + 1,
            },
            {
                key: "productName",
                header: "Kode Stock",
                render: (row) => row.brandName + ' ' + row.productName,
            },
            {
                key: "size",
                header: "Size"
            },
            {
                key: "stockStatus",
                header: "Stock Status"
            },
            {
                key: "totalStock",
                header: "Total Stock",
            },
        ]

    // Button Event
    const addBtnClick = () => {
        navigate("/sales-stock/form?salesStockId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (id) => {
        navigate("/sales-stock/form?salesStockId=" + id + "&formType=" + FormTypeEdit)
    }
    const deleteBtnClick = async (id, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            const res = await fetchDeleteSalesStock(id, status)
            if (!res) return

            showSuccess({
                onSuccess: () => {
                    handlePageChange(0)
                }
            })
        }
        finally {
            hideLoading()
        }
    }

    // Function Grid
    const handlePageChange = async (newIndex) => {
        showLoading()

        try {
            const res = await fetchSalesStockList(productName, brand === null || brand === '' ? '' : brand.value, stockCode, stockStatus, status, groupBy === '1' ? PageSize : 10000, newIndex, groupBy)
            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize))

            setSalesStockList(res)
            setTotalPages(total)
            setPageIndex(newIndex)
        } finally {
            hideLoading()
        }
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                showLoading()

                try {
                    const res = await fetchBrandList('', '1', 1000, 0)
                    if (!res) return

                    let data = res?.data?.listData
                    let tempData = [{ label: 'All', value: '' }]

                    data.map((data) => tempData.push({ label: data.brandName, value: String(data.brandId) }))

                    setBrandOptions(tempData)
                }
                finally {
                    handlePageChange(pageIndex)
                    hideLoading()
                    firstLoad.current = false
                }
            } else {
                handlePageChange(pageIndex)
            }
        }

        onLoad()
    }, [brand, productName, stockStatus, status, groupBy])

    const exportPdfBtnClick = async () => {
        showLoading()

        try {
            const res = await fetchSalesStockList(productName, brand === null || brand === '' ? '' : brand.value, stockCode, stockStatus, status, DownloadPageSize, 0, groupBy)
            if (!res) return

            exportSalesStockToPdf(res?.data?.listData ?? [], { groupBy })
        }
        finally {
            hideLoading()
        }
    };

    return (
        <MainBodyCard>
            <PagesTitle title='Stock Penjualan' />

            <div className="flex flex-col sm:flex-row justify-between gap-8 mt-2">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Brand' width='w-72'>
                        <Select options={brandOptions} value={brand} onChange={(e) => { setBrand(e) }} placeholder='All' />
                    </FilterCard>

                    <FilterCard title='Nama Product' width='w-72'>
                        <input className="input" value={productName} onChange={(e) => { setProductName(e.target.value) }} />
                    </FilterCard>

                    <FilterCard title='Status' width='w-32'>
                        <Dropdown value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder='All' />
                    </FilterCard>
                </div>
            </div>

            <div className="flex w-full justify-between bg-white p-2 rounded-xl shadow-md text-sm border mt-5">
                <div className="flex gap-4 flex-wrap">
                    <div className="flex gap-2">
                        {SalesStockStatusOptions.map((data, i) => {
                            return (
                                <div className={`border px-3 flex items-center rounded-full cursor-pointer ${stockStatus === data.value ? 'bg-black text-white' : ''} `}
                                    onClick={() => { setStockStatus(data.value) }}
                                    key={i}
                                >
                                    {data.label}
                                </div>
                            )
                        })}
                    </div>
                    <div className="border-x" />
                    <div className="flex gap-2">
                        {SalesStockGroupByOptions.map((data, i) => {
                            return (
                                <div className={`border px-3 flex items-center rounded-full cursor-pointer ${groupBy === data.value ? 'bg-black text-white' : ''} `}
                                    onClick={() => { setGroupBy(data.value) }}
                                    key={i}
                                >
                                    <span className="text-sm">{data.label}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="flex gap-2">
                    <PrintButton
                        className="px-4 py-2 rounded-full border bg-white hover:bg-slate-100 whitespace-nowrap"
                        onClick={exportPdfBtnClick}
                    >
                        Export PDF
                    </PrintButton>
                    <AddButton onClick={addBtnClick} text='Tambah Stock' />
                </div>
            </div>

            {/* WIDE SCREEN */}
            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={salesStockList?.data?.listData ?? []}
                />
            </div>

            {/* MOBILE SCREEN */}
            <div className="mt-2 overflow-hidden sm:border sm:rounded-3xl sm:shadow-sm sm:hidden gap-2 flex flex-col">
                {groupBy === '1' ?
                    (
                        salesStockList?.data?.listData?.length === 0 ? (
                            <div className="text-center h-12">
                                <span>No Data Available</span>
                            </div>
                        ) :

                            salesStockList?.data?.listData?.map((data, i) => {
                                return (
                                    <div className="p-2 border rounded-lg hover:bg-slate-100" key={i}>
                                        <div className="flex flex-col">
                                            <span>{data.brandName} {data.productName}</span>
                                            <span>{data.stockCode}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Size: {data.size}</span>
                                        </div>
                                    </div>
                                )
                            })
                    ) : (
                        <div className="flex flex-col gap-2">
                            {salesStockList?.data?.listData?.length === 0 && (
                                <div className="text-center h-12">
                                    <span>No Data Available</span>
                                </div>
                            )}
                            {salesStockList?.data?.listData?.map((data, i) => {
                                return (
                                    <div className="flex justify-between border rounded-lg p-2 hover:bg-slate-100" key={i}>
                                        <div>
                                            <div className="px-2">
                                                <span>{data.brandName} {data.productName}</span>
                                            </div>
                                            <div className="px-2 hidden sm:table-cell">
                                                <span>{SalesStockStatusOptions.find(x => x.value === String(data.stockStatus)).label}</span>
                                            </div>
                                            <div className="px-2 hidden sm:table-cell">
                                                <span>{data.totalStock}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span>{data.totalStock}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                }
            </div>

            <Pagination
                pageIndex={pageIndex}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </MainBodyCard>
    )
}